/**
 * ScenarioDisclaimerFooter — regulatory CYA on planner pages.
 *
 * Renders the canonical scenario disclaimer (server-side string from
 * `/api/disclaimers/copy`) on every planning page for non-owner-tier
 * users. Owner tier doesn't see it (Simon + Unman self-direct).
 *
 * The string itself is fetched once and cached in this module so every
 * planning page can drop the component without N redundant requests.
 */
import { useEffect, useState } from 'react'
import { ShieldAlert } from 'lucide-react'

import { getDisclaimerCopy } from '@/lib/api'
import { useCapabilities } from '@/hooks/useCapabilities'
import type { DisclaimerCopy } from '@/lib/types'

let _cached: DisclaimerCopy | null = null
let _inFlight: Promise<DisclaimerCopy> | null = null

async function fetchOnce(): Promise<DisclaimerCopy> {
  if (_cached) return _cached
  if (_inFlight) return _inFlight
  _inFlight = getDisclaimerCopy().then((c) => {
    _cached = c
    _inFlight = null
    return c
  }).catch((e) => {
    _inFlight = null
    throw e
  })
  return _inFlight
}

export default function ScenarioDisclaimerFooter() {
  const isOwner = useCapabilities((s) => s.capabilities?.is_owner ?? false)
  const tierLoaded = useCapabilities((s) => s.capabilities !== null)
  const [copy, setCopy] = useState<DisclaimerCopy | null>(_cached)

  useEffect(() => {
    if (isOwner) return  // owner tier never sees the footer
    if (copy) return
    void fetchOnce().then(setCopy).catch(() => {})
  }, [isOwner, copy])

  if (!tierLoaded || isOwner || !copy) return null

  return (
    <div
      role="contentinfo"
      className="mt-8 mb-2 px-4 py-3 rounded-lg border border-border/60 bg-surface-card/40 flex items-start gap-2.5 text-[11px] text-text-muted leading-relaxed"
    >
      <ShieldAlert size={13} className="shrink-0 mt-0.5 text-amber" aria-hidden />
      <span>{copy.scenario}</span>
    </div>
  )
}
